import { createContext, useContext } from 'react';

import { useDraggable, useDroppable } from '@dnd-kit/core';
import { cn } from '@mint-lib/tw-utils';

import type { MoveVerdict } from '../../../../helpers/fileSystemMove.js';
import type { FileSystemNode } from '../../../../types/fileSystem.js';
import { toDragId, toDropId } from './dndIds.js';

export type FileSystemDndValue = {
  activeNodes: FileSystemNode[];
  overFolderId: string | null;
  isOverAllowed: boolean;
  // Exposed on the context because the grid drags rows through AG Grid rather
  // than dnd-kit, and both mechanisms have to answer to the same rules.
  checkMove: (nodes: FileSystemNode[], targetFolderId: string) => MoveVerdict;
  onMoveNodes: (nodes: FileSystemNode[], targetFolderId: string) => void;
};

export const FileSystemDndContext = createContext<FileSystemDndValue>({
  activeNodes: [],
  overFolderId: null,
  isOverAllowed: false,
  checkMove: () => ({ allowed: false, reason: 'missing-target' }),
  onMoveNodes: () => {},
});

export const useFileSystemDnd = () => useContext(FileSystemDndContext);

export const useFolderDropZone = (folderId: string) => {
  const { activeNodes, overFolderId, isOverAllowed } =
    useContext(FileSystemDndContext);
  const { setNodeRef } = useDroppable({ id: toDropId(folderId) });

  const isOver = overFolderId === folderId;

  return {
    setDropRef: setNodeRef,
    isOver,
    isDropAllowed: isOver && isOverAllowed,
    isDropRejected: isOver && !isOverAllowed,
    isDragging: activeNodes.length > 0,
  };
};

export const useNodeDragSource = (node: FileSystemNode) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: toDragId(node),
    data: { node },
  });

  return {
    setDragRef: setNodeRef,
    dragHandleProps: { ...attributes, ...listeners },
    isDragging,
  };
};

export const dropZoneClasses = ({
  isDropAllowed,
  isDropRejected,
}: {
  isDropAllowed: boolean;
  isDropRejected: boolean;
}) =>
  cn(
    'transition-colors',
    // border-width is deliberate: this is reused on surfaces that have no
    // border of their own (the breadcrumb button), where a bare border-color
    // would paint nothing.
    isDropAllowed && 'border border-tertiary-main bg-background-tertiary',
    isDropRejected && 'border border-error-main opacity-60',
  );
